import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import { api, canonicalBase } from "../lib/api";
import SEO from "../components/SEO";

export default function Contact() {
  const [settings, setSettings] = useState(null);
  const [page, setPage] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/site-settings?_=${Date.now()}`).then((r) => r.data).catch(() => null),
      api.get(`/pages/kontakt?_=${Date.now()}`).then((r) => r.data).catch(() => null),
    ]).then(([s, p]) => { setSettings(s); setPage(p); });
  }, []);

  const heading = page?.title || "Jsme tady pro vás";
  const intro = page?.content || "Napište nám na e-mail a my se vám ozveme do 24 hodin v pracovní dny.";
  const email = settings?.footer_email || "info@spravnysmer.eu";
  const phone = settings?.footer_phone || "";
  const address = settings?.footer_city || "";

  return (
    <div className="pt-8 pb-20" data-testid="contact-page">
      <SEO
        title="Kontakt – jsme tu pro vás"
        description={`Kontaktujte nás – e-mail ${email}. Odpovídáme do 24 hodin v pracovní dny.`}
        keywords="kontakt, podpora, e-mail, spravnysmer"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Kontakt",
          url: canonicalBase() + "/kontakt",
        }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center sm:text-left">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-2">Kontakt</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{heading}</h1>
          <div className="text-zinc-600 mt-3 whitespace-pre-wrap text-base">{intro}</div>
        </div>

        {/* Primary CTA — e-mail card */}
        <a
          href={`mailto:${email}`}
          data-testid="contact-email-cta"
          className="group block bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-3xl p-8 sm:p-10 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                <Mail className="w-7 h-7"/>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-100 font-semibold mb-1">Napište nám</div>
                <div className="font-display text-2xl sm:text-3xl font-bold break-all">{email}</div>
                <div className="text-blue-100 text-sm mt-2">Klikněte pro otevření vašeho e-mailového klienta</div>
              </div>
            </div>
            <ArrowUpRight className="w-6 h-6 opacity-70 group-hover:opacity-100 group-hover:rotate-12 transition-transform shrink-0"/>
          </div>
        </a>

        {/* Secondary info */}
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {phone && (
            <a href={`tel:${phone.replace(/\s+/g,'')}`} className="bg-white border border-zinc-200 rounded-2xl p-5 flex gap-3 items-start hover:border-blue-300 transition-colors" data-testid="contact-phone">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-blue-600"/></div>
              <div><div className="text-xs text-zinc-500">Telefon</div><div className="font-semibold">{phone}</div></div>
            </a>
          )}
          {address && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-5 flex gap-3 items-start">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-blue-600"/></div>
              <div><div className="text-xs text-zinc-500">Adresa</div><div className="font-semibold">{address}</div></div>
            </div>
          )}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-blue-600"/></div>
            <div><div className="text-xs text-zinc-500">Odezva</div><div className="font-semibold">do 24 hodin (Po–Pá)</div></div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-blue-600"/></div>
            <div><div className="text-xs text-zinc-500">Online chat</div><div className="font-semibold">Po–Pá 9:00–17:00</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
